import type { Asset } from "contentful"
import { Logger } from "lib/logger"
import type { ProductCategoryType, VariantType } from "types"
import type { DerivativeName, BynderAsset, BynderAssetDetails } from "../bynder"
import type { Category, SubCategory } from "../category"

export type ImageParams = {
  src: string
  alt: string
  title: string
  width: number
  height: number
}

type ProductCarouselSharedType = {
  title: string
  backgroundColour: string
  layout: string
}

type ProductCarouselArgs = ProductCarouselSharedType & {
  variants: { fields: Record<string, string> }[]
}

export type ProductCarouselType = ProductCarouselSharedType & {
  variants: VariantType[]
}

type ImageAsset = Asset | BynderAsset

type OptString = string | undefined

const nullImage: ImageParams = {
  src: "data:,x",
  height: 0,
  width: 0,
  alt: "",
  title: "",
}

export const DEFAULT_DERIVATIVE: DerivativeName = "16_9"

const isBynderAsset = (value: ImageAsset): value is BynderAsset =>
  "bynderData" in value && typeof value.bynderData === "object"

// solves inconsitency in the current naming convention in bynder
const getDerivative = (
  byimg: BynderAsset,
  derivative: DerivativeName = DEFAULT_DERIVATIVE
): Array<OptString> => {
  const details: BynderAssetDetails | undefined = byimg.bynderData?.[0]
  if (!details) return []
  const extensions: string[] = details?.extension ?? []

  // serve gif without conversions, but the asset must be public in Bynder
  if (extensions[0] === "gif") {
    if (!details.original) {
      Logger.warn(
        `Gif file must be made public in Bynder: ${byimg.id}. Serving png as a fallback`
      )
      return ["webimage", details.thumbnails.webimage]
    }
    return ["gif", details.original]
  }

  // serve pngs without conversion
  if (extensions[0] === "png") return ["webimage", details.thumbnails.webimage]

  const widescreenVariants: DerivativeName[] = [
    "16_9",
    "16_9_web",
    "widescreen",
  ]
  const portraitVariants: DerivativeName[] = ["4_5", "4_5_web", "mobilehero"]

  const names: DerivativeName[] = [
    derivative,
    ...(widescreenVariants.includes(derivative) ? widescreenVariants : []),
    ...(portraitVariants.includes(derivative) ? portraitVariants : []),
    DEFAULT_DERIVATIVE,
    "web",
    "2000",
    "OTF BETA",
    "webimage",
    "online",
  ]
  for (const name of names)
    if (name in details.thumbnails) {
      return [name, details.thumbnails[name]]
    }
  return []
}

interface ImageSize {
  width: number
  height: number
}

// ugly, but bynder provides only the sizes of original files, not derivatives
const getImageSize = (derivativeName: OptString, def: ImageSize): ImageSize => {
  switch (derivativeName) {
    case "16_9":
      return { width: 2500, height: 1408 }
    case "16_9_web":
      return { width: 1280, height: 720 }
    case "4_5":
      return { width: 2000, height: 2500 }
    case "4_5_web":
      return { width: 768, height: 960 }
    default:
      return def
  }
}

const getBynderImageParams = (
  imgData: BynderAsset,
  derivative: DerivativeName = DEFAULT_DERIVATIVE
): ImageParams => {
  const derivativeName = derivative

  if (!imgData) return nullImage
  if (!derivativeName) {
    Logger.warn(`Unsupported derivative: ${derivativeName}`)
    return nullImage
  }

  const [dname, src] = getDerivative(imgData, derivativeName)
  if (!src) return nullImage

  const { width, height } = getImageSize(dname, {
    width: imgData?.bynderData?.[0]?.width ?? 0,
    height: imgData?.bynderData?.[0]?.height ?? 0,
  })

  return {
    src: src.replace(/^\/\//, "https://"),
    width,
    height,
    alt: imgData?.description ?? "",
    title: imgData?.filename ?? "",
  }
}

const getContentfulAssetParams = (imgData: Asset): ImageParams => {
  if (!imgData) return nullImage

  const src = imgData?.fields?.file?.url
  if (!src) return nullImage

  const [width, height] = [
    imgData?.fields?.file?.details?.image?.width ?? 0,
    imgData?.fields?.file?.details?.image?.height ?? 0,
  ]

  return {
    src: src.replace(/^\/\//, "https://"),
    width,
    height,
    alt: imgData?.fields?.description ?? "",
    title: imgData?.fields?.title ?? "",
  }
}

export const getImageParams = (
  imgData: ImageAsset,
  derivative: DerivativeName = DEFAULT_DERIVATIVE
): ImageParams => {
  // eslint-disable-next-line no-nested-ternary
  const img = !imgData
    ? nullImage
    : isBynderAsset(imgData)
    ? getBynderImageParams(imgData, derivative)
    : getContentfulAssetParams(imgData)

  if (img === nullImage) {
    Logger.warn(
      "Requested image not found. Serving empty image instead",
      imgData,
      derivative
    )
  }

  return img
}

export const isSubCategory = (
  category:
    | Category
    | SubCategory
    | ProductCategoryType
    | { parent?: { slug: string } }
): category is SubCategory =>
  "parent" in category && typeof category.parent !== "undefined"

export const parseProductCarousel = (
  productCarousel: ProductCarouselArgs
): ProductCarouselType | undefined => {
  if (!productCarousel) return
  const variants = productCarousel.variants.map(
    (variant) => variant?.fields.sku
  )
  return {
    title: productCarousel.title,
    backgroundColour: productCarousel.backgroundColour,
    layout: productCarousel.layout,
    variants: variants as unknown as VariantType[],
  }
}
